// React
import { PropsWithChildren, useState } from "react"

// Components
import { BaseTable, Button, Card, Checkbox, DateInput, HeaderMenu, Input, Label, Modal, Select, TabSection, TableHeader, TableSection, Textarea } from "components"
import { Detail as RecordRejectDetail } from "../../RecordReject/components"
import { Code } from "./Code"
import { DetailReject } from "./DetailReject"
import { ReceivedDetail } from "./ReceivedDetail"

// Contenxts
import { DataContext, DetailContext, DropdownProvider } from "../contexts"

// Form
import { Controller, useFieldArray, useFormContext } from "react-hook-form"
import { ErrorMessage } from "@hookform/error-message"

// Icons
import { TbInfoCircle } from "react-icons/tb"

// Third-Party Libraries
import moment from "moment"
import toast from "react-hot-toast"

// Types
import type { DataType } from "../types"
import type { DetailType, FormType } from "../utils"

// Utils
import { convertNumber, useLocation, useToggle } from "utils"
import { default_option } from "pages/Purchase/utils"
import { useSupplier } from "../../components/Supplier/utils"
import { billStatusList } from "../../EnterBill/utils"
import { getSum, receiveType } from "../utils"
import { convertDate } from "pages/Inventori/Transaction/TransactionJournal/utils"

export function FormSection(params: {
  data: DataType
  is_detail?: boolean
  isPrint?: boolean
}): JSX.Element {
  // Vars
  const is_detail = params.is_detail ?? false;
  const data = params.data
  const detailreject = data.detailreject


  // Form
  const { control, formState, watch } = useFormContext<FormType>()
  const { fields, append, update } = useFieldArray({
    control,
    name: "received_reject_detail"
  })

  const receivedDetail = watch("received_reject_detail")
  const sum = getSum(receivedDetail) 

  // Functions
  const onAdd = (value: DetailType): void => {
    if (fields.find(item => item.product_id === value.product_id)) {
      toast.error("Data has already added into detail table")
      return
    }

    append(value)
  }

  return (
    <>
      <HeaderMenu title="DATA ENTRY | REJECT - RECEIVED REJECT ITEM">
        {is_detail ? (
          <div>{data.trx_code}</div>
        ):(
          <Code
            reject_code={data.trx_code}
            jenis={receiveType}
          />
        )}
        
      </HeaderMenu>

      <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3 items-end">
        <div className="w-[400] my-4">
          <Supplier isPrint={params?.isPrint} vendor_id={data.vendor_id} />
        </div>

        {!params?.isPrint && <section className="lg:justify-self-end mb-3">
          <Checkbox
            readOnly
            value
          >
            TAX-INCLUSIVE
          </Checkbox>
        </section>}
      </section>

      <Card>
        <Card.Body className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
          <section className="flex flex-col gap-3">
            <section>
              <Deliver isPrint={params?.isPrint} location_id={data.location_id} />
              {params?.isPrint && <InfoItems label="ADDRESS" width={250} text={data.address} />}
              {!params?.isPrint && <Textarea disabled value={data.address} />}
              {params?.isPrint && <InfoItems label="DATE REJECT" width={250} text={data.transaction_date ? convertDate(data.transaction_date) : "-"} />}
              {params?.isPrint && <InfoItems label="LINK TO REJECT ID" width={250} text={data.trx_code} />}
            </section>

            {!params?.isPrint && <DateInput
              disabled
              label="DATE REJECT"
              selected={data.transaction_date ? new Date(data.transaction_date) : null}
              onChange={() => {}}
            />}

            <section className="flex items-end">
              <section className="grow">
                {!params?.isPrint && <Input
                  disabled
                  inputClass="rounded-r-none"
                  label="LINK TO REJECT ID"
                  value={data.trx_code}
                />}
              </section>

              {!params?.isPrint && <LinkToReject reject_id={data.reject_id} />}
            </section>
          </section>

          <section className="flex flex-col gap-3">
            <Controller
              name="transaction_date"
              control={control}
              render={({ field, fieldState }) => (
                <div className="w-full">
                  {
                    params?.isPrint ? <InfoItems label="DATE" width={250} text={field.value ? convertDate(field.value) : "-"} /> :
                    <DateInput
                      label="DATE"
                      disabled={field.disabled}
                      error={fieldState.error?.message}
                      selected={new Date(field.value)}
                      ref={field.ref}
                      onChange={(res) => field.onChange(moment(res).format("YYYY-MM-DD"))}
                    />
                  }
                </div>
              )}
            />

            <Controller
              name="referensi"
              control={control}
              render={({ field, fieldState }) => (
                <div className="w-full">
                  {
                    params?.isPrint ? <InfoItems label="REFERENCE" width={250} text={field.value} /> :
                    <Input
                      {...field}
                      label="REFERENCE"
                      disabled={field.disabled}
                      error={fieldState.error?.message}
                    />
                  }
                </div>
              )}
            />

            <Controller
              name="memo"
              control={control}
              render={({ field, fieldState }) => (
                <div className="w-full">
                  {
                    params?.isPrint ? <InfoItems label="MEMO" width={250} text={field.value} /> :
                    <Textarea
                      {...field}
                      label="MEMO"
                      disabled={field.disabled}
                      error={fieldState.error?.message}
                    />
                  }
                </div>
              )}
            />
          </section>
        </Card.Body>
      </Card>

      <DropdownProvider>
        <BaseTable>
          <TableHeader>
            <tr>
              <th>BACK DELIVERED</th>
              <th className="w-[150px]">QTY RECEIVED</th>
              <th>UNIT</th>
              <th>ITEM CODE</th>
              <th>DESCRIPTION</th>
              <th>PRICE</th>
              <th>DISC (%)</th>
              <th>TOTAL</th>
              <th>TAX</th>
              <th>ACTION</th>
            </tr>
          </TableHeader>

          <DetailContext.Provider value={{ fields, update }}>
            <ReceivedDetail />
          </DetailContext.Provider>
        </BaseTable>

        <ErrorMessage
          name="received_reject_detail"
          errors={formState.errors}
          render={({ message }) => (
            <Label
              error
              text={message}
            />
          )}
        />

        <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
          <section className="flex flex-col gap-3">
            <TableSection>
              <TableSection.Row
                label="REJECT STATUS"
                value={billStatusList[data.reject_status.toString()]}
              />

              <TableSection.Row
                label="APPROVE STATUS"
                value={default_option.find((item) => item.value === data.approve_status.toString())?.label ?? "-"}
              />

              <TableSection.Row
                label="APPROVED BY"
                value={data.approve_by}
              />
            </TableSection>
          </section>

          <section className="flex flex-col lg:items-end gap-3">
            <BaseTable
              plain
              className="w-full min-w-[100px] md:max-w-[400px] text-right">
              <tbody>
                <tr>
                  <td className="w-[1px] whitespace-nowrap">SUBTOTAL</td>
                  <td>{convertNumber(sum.subtotal).intoCurrency}</td>
                </tr>

                <tr>
                  <td className="w-[1px] whitespace-nowrap">TAX</td>
                  <td>{convertNumber(sum.tax).intoCurrency}</td>
                </tr>

                <tr className="font-bold">
                  <td className="w-[1px] whitespace-nowrap">TOTAL RECEIVED</td>
                  <td>{convertNumber(sum.subtotal + sum.tax).intoCurrency}</td>
                </tr>
              </tbody>
            </BaseTable>
          </section>
        </section>

        <DataContext.Provider
          value={{
            detailreject,
            onAddDetail(value) {
              onAdd(value)
            }
          }}
        >
          <Tab />
        </DataContext.Provider>
      </DropdownProvider>
    </>
  )
}

function Deliver(params: {
  location_id: number
  isPrint?: boolean
}): JSX.Element {
  // Hooks
  const { data, isLoading } = useLocation()

  return (
    <>
      {
        params?.isPrint
        ? <InfoItems uppercase label="DELIVER TO" width={250} text={data.find(item => item.value === params.location_id.toString())?.label!} />
        : (
          <Select
            isDisabled
            label="DELIVER TO"
            placeholder="Select Deliver"
            isLoading={isLoading}
            options={data}
            value={data.find(item => item.value === params.location_id.toString()) ?? null}
          />
        )
      }
    </>
  )
}

function LinkToReject(params: {
  reject_id: number
}): JSX.Element {
  // Hooks
  const { isActive, toggle } = useToggle(false)

  return (
    <>
      <Button
        type="button"
        color="primary"
        onClick={toggle}
      >
        <TbInfoCircle />
      </Button>

      {isActive && (
        <Modal
          isOpen
          title="REJECT ITEM DETAIL"
          closeModal={toggle}
        >
          <Modal.Body>
            <RecordRejectDetail id={params.reject_id.toString()} />
          </Modal.Body>
        </Modal>
      )}
    </>
  )
}

function Supplier(params: {
  vendor_id: number
  isPrint?: boolean
}): JSX.Element {
  // Hooks
  const { data, isLoading } = useSupplier()

  return (
    <>
      {
        params?.isPrint
        ? <InfoItems uppercase label="SUPPLIER" width={150} text={data.find(item => item.value === params.vendor_id.toString())?.label!} />
        : (
          <Select
            isDisabled
            label="SUPPLIER"
            placeholder="Select Supplier"
            isLoading={isLoading}
            options={data}
            value={data.find(item => item.value === params.vendor_id.toString()) ?? null}
          />
        )
      }
    </>
  )
}

function Tab(): JSX.Element {
  // Vars
  const tabList: string[] = ["DETAIL REJECT"]

  // Hooks
  const [currentTab, setCurrentTab] = useState<number>(0)

  return (
    <section>
      <TabSection
        activeIndex={currentTab}
        list={tabList}
        onChangeTab={setCurrentTab}
      />

      {Boolean(currentTab === 0) && <DetailReject />}
    </section>
  )
}

type InfoItemsType = {
  label: string
  text: string | number
  uppercase?: boolean
  textRight?: boolean
  width?: number
  textWidth?: number
}
const InfoItems = (props: PropsWithChildren<InfoItemsType>) => (
  <tr>
    <td width={props?.width ? props?.width : 120} className="pr-2 uppercase flex justify-between">
      <div>{props?.label}</div>
      <div>:</div>
    </td>
    <td width={props?.textWidth} className={props?.uppercase ? `uppercase` : props?.textRight ? "text-right" : ""}>{props?.text || "-"}</td>
  </tr>
)